import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import axiosInstance from 'apis/axiosConfig';
import { useNavigate } from 'react-router-dom';

const LoanProviders = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('loan-providers');
            if (res?.data && Array.isArray(res.data)) {
                setData(res.data);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error("Error fetching loan providers:", err);
            toast.error("Failed to load loan providers list.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, []);



    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to permanently delete this Loan Provider?")) {
            try {
                await axiosInstance.delete(`loan-providers/${id}`);
                toast.success("Loan Provider deleted successfully!");
                fetchData();
            } catch (err) {
                console.error("Delete failed:", err);
                toast.error("Failed to delete Loan Provider.");
            }
        }
    };

    const tableHeaders = [
        { Header: 'ID', accessor: 'id', Cell: ({ row }) => <strong style={{ color: '#0284C7' }}>#{row.original.id}</strong> },
        { Header: 'Name', accessor: 'name', Cell: ({ row }) => <span className="font-weight-bold" style={{ color: '#0F172A' }}>{row.original.name}</span> },
        { Header: 'Email / Username', accessor: 'email', Cell: ({ row }) => <span className="text-muted font-weight-bold">{row.original.email}</span> },
        { 
            Header: 'Status', 
            accessor: 'status', 
            Cell: ({ row }) => {
                const isActive = row.original.status === true || row.original.status === 1;
                return (
                    <span 
                        className={`badge font-weight-bold py-2 px-3`}
                        style={{
                            borderRadius: '20px',
                            backgroundColor: isActive ? '#D1FAE5' : '#FEE2E2',
                            color: isActive ? '#065F46' : '#991B1B',
                            border: `1px solid ${isActive ? '#A7F3D0' : '#FCA5A5'}`
                        }}
                    >
                        {isActive ? '✓ Active' : '✗ Suspended'}
                    </span>
                );
            }
        },
        { 
            Header: 'Onboarded Date', 
            accessor: 'createdAt', 
            Cell: ({ row }) => {
                const date = row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : 'N/A';
                return <span style={{ color: '#64748B', fontWeight: '600' }}>{date}</span>;
            }
        },
        { 
            Header: 'Actions', 
            accessor: 'action', 
            Cell: ({ row }) => {
                return (
                    <div className="d-flex justify-content-center align-items-center">
                        <button 
                            type="button"
                            className="btn btn-sm font-weight-bold shadow-sm mr-2" 
                            style={{ backgroundColor: '#E2F1F1', color: '#082032', borderRadius: '8px', padding: '6px 12px', border: 0 }}
                            onClick={() => navigate(`/loan-providers/${row.original.id}`)}
                        >
                            🔍 View Ledgers
                        </button>
                        <button 
                            type="button"
                            className="btn btn-sm font-weight-bold shadow-sm" 
                            style={{ backgroundColor: '#FEE2E2', color: '#DC2626', borderRadius: '8px', padding: '6px 12px', border: 0 }}
                            onClick={() => handleDelete(row.original.id)}
                        >
                            🗑️ Delete
                        </button>
                    </div>
                );
            } 
        }
    ];

    return (
        <div className="container-fluid mt-4 mb-5" style={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
            {/* Header */}
            <div className="mb-4 px-2 d-flex justify-content-between align-items-center">
                <div>
                    <span className="badge px-3 py-1 mb-2 font-weight-bold" style={{ borderRadius: '8px', backgroundColor: '#F3E8FF', color: '#7E22CE' }}>
                        SYSTEM SECURITY ACCESS CONTROL
                    </span>
                    <h2 className="font-weight-bold mb-1" style={{ color: '#0F172A' }}>Loan Providers Management</h2>
                    <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>Onboard, review status, and inspect sub-ledgers for all active Loan Providers.</p>
                </div>
                <div>
                    <button 
                        className="btn btn-outline-primary font-weight-bold px-3 py-2 shadow-sm mr-2"
                        onClick={fetchData}
                        disabled={loading}
                        style={{ borderRadius: '8px', fontSize: '13px' }}
                    >
                        {loading ? 'Refreshing...' : '🔄 Force Sync'}
                    </button>
                    <button 
                        className="btn btn-primary font-weight-bold px-3 py-2 shadow-sm"
                        onClick={() => navigate('/loan-providers/add')}
                        style={{ borderRadius: '8px', background: 'linear-gradient(90deg, #0EA5A4 0%, #0B4F6C 100%)', border: 0, fontSize: '13px' }}
                    >
                        + Add Loan Provider
                    </button>
                </div>
            </div>

            <div className="row">
                {/* Table list */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm bg-white p-4" style={{ borderRadius: '20px' }}>
                        <h4 className="font-weight-bold text-dark mb-4">Active Providers List</h4>
                        {loading && data.length === 0 ? (
                            <div className="text-center py-5 my-5">
                                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <h5 className="font-weight-bold mt-3 text-secondary">Fetching Registered Providers...</h5>
                            </div>
                        ) : data.length > 0 ? (
                            <div className="animate__animated animate__fadeIn">
                                <AdminTable tableHeaders={tableHeaders} tableData={data} />
                            </div>
                        ) : (
                            <div className="text-center py-5 my-4 bg-light rounded-lg border">
                                <span style={{ fontSize: '40px' }}>👥</span>
                                <h5 className="font-weight-bold mt-2 text-dark">No Loan Providers Registered</h5>
                                <p className="text-muted mb-0">Use the form on the left to onboard your first Loan Provider.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoanProviders;

